import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button, Card, Container } from "react-bootstrap";
import useAccess from "../../hooks/useAccess";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const formatDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "-" : dt.toLocaleDateString();
};

export default function AnnualDriverCertificationList() {
  const navigate = useNavigate();
  const { id: paramId } = useParams();

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const { hasPermission } = useAccess();
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const canDeleteRecords = isAdmin && hasPermission("Task", "Task", "delete");

  const [records, setRecords] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/annual-driver-certifications/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setRecords(res.data || []);
      } catch (err) {
        console.error("❌ ADC list fetch error", err);
      }
    };
    fetchData();
  }, [userId]);

  const handleDeleteRecord = async (recordId) => {
    if (!canDeleteRecords || !recordId) return;
    if (!window.confirm("Delete this record? This cannot be undone.")) return;

    try {
      await axios.delete(`${API_BASE}/api/annual-driver-certifications/${recordId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords((prev) => prev.filter((row) => row.adc_id !== recordId));
    } catch (err) {
      console.error("ADC delete failed", err);
      alert(err?.response?.data?.message || "Failed to delete record.");
    }
  };

  const handleReturn = () => {
    if (isAdmin && paramId) navigate(`/userhome/${paramId}`);
    else navigate("/userhome");
  };

  const rows = useMemo(() => records ?? [], [records]);

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Annual Driver Certification of Violations</h3>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={handleReturn}>
            Return
          </Button>
          <Button
            variant="success"
            onClick={() =>
              navigate(`/annual-driver-certifications/${userId}/new`)
            }
          >
            Add
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          {!rows.length ? (
            <div className="text-muted text-center">No records found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "6%" }}>#</th>
                    <th style={{ width: "15%" }}>Date Saved</th>
                    <th style={{ width: "20%" }}>Saved By</th>
                    <th style={{ width: "15%" }}>Date Created</th>
                    <th style={{ width: "20%" }}>Created By</th>
                    <th style={{ width: "14%" }}>Violations</th>
                    <th style={{ width: "110px" }} className="text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.adc_id}>
                      <td>{idx + 1}</td>
                      <td>{formatDate(r.adc_saved_date)}</td>
                      <td>{r.saved_by || "-"}</td>
                      <td>{formatDate(r.adc_created_date)}</td>
                      <td>{r.created_by || "-"}</td>
                      <td>{r.adc_no_violations ? "No" : "Yes"}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            data-print-record-path={`/annual-driver-certifications/${userId}/detail/${r.adc_id}`}
                            onClick={() =>
                              navigate(
                                `/annual-driver-certifications/${userId}/detail/${r.adc_id}`
                              )
                            }
                          >
                            Edit
                          </Button>
                          {canDeleteRecords && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeleteRecord(r.adc_id)}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

